import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DeckingBoardsService } from 'src/app/services/configurators-2d/applications/decking-boards.service';
import { BaseConfigurator } from '../configurator/base-configurator';
import { ConfiguratorStructure } from '../configurator/configurator-structure';
import {
  DB_ACCESSORIES,
  DB_CONFIGURATION,
  DB_ENVIRONMENT,
  DB_FINISHES,
  DB_SUMMARY,
} from 'src/app/providers/decking-boards.providers';
import { Configuration } from 'src/app/models/configurators-2d/applications/decking-boards/configuration';
import { TypeLocationEnum } from 'src/app/enum/decking-boards/type-location.enum';

@Component({
  selector: 'ng-decking-boards-configuration',
  templateUrl: './decking-boards.component.html',
})
export class DeckingBoardsComponent extends BaseConfigurator<Configuration, DeckingBoardsService> implements OnInit {
  //Etape de la configuration
  @Input() step: number = 1;

  @Output() setGlobalLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() getFinishesStep: EventEmitter<any> = new EventEmitter<boolean>();

  //URL de base des images
  baseImgURL: string = undefined;

  // Loading local
  isLoading: boolean = false;

  constructor(
    private route: ActivatedRoute,
    deckingBoardsService: DeckingBoardsService,
    injector: Injector
  ) {
    super(deckingBoardsService, injector);
    this.baseImgURL = this.service.apiUrl;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.loadExistingConfigurationIfNeeded(this.route).then(() => {
      this.setGlobalLoading.emit(false);
      this.isLoading = false;
    });
  }

  ngOnDestroy() {
    this.service.reset();
  }

  override initConfigurator(): ConfiguratorStructure {
    return new ConfiguratorStructure([
      {
        index: 1,
        title: 'decking-boards.steps.environment',
        injectionToken: DB_ENVIRONMENT,
        gtmTitle: 'Terrasse | Votre Environnement',
        gtmPageName: 'votre-environnement',
      },
      {
        index: 2,
        title: 'decking-boards.steps.configuration',
        injectionToken: DB_CONFIGURATION,
        gtmTitle: 'Terrasse | Mesures',
        gtmPageName: 'mesures',
      },
      {
        index: 3,
        title: 'decking-boards.steps.finishes',
        injectionToken: DB_FINISHES,
        gtmTitle: 'Terrasse | Matières et Couleurs',
        gtmPageName: 'matieres-et-couleurs',
      },
      {
        index: 4,
        title: 'decking-boards.steps.accessories',
        injectionToken: DB_ACCESSORIES,
        gtmTitle: 'Terrasse | Accessoires',
        gtmPageName: 'accessoires',
      },
      {
        index: 5,
        title: 'decking-boards.steps.summary',
        injectionToken: DB_SUMMARY,
        gtmTitle: 'Terrasse | Prix et Résumé',
        gtmPageName: 'prix-et-resume',
      },
    ]);
  }

  /**
   * Fonction de check si la configuration est complète
   */
  checkIsComplete() {
    let configuration = this.configuration;

    let checkRaisedHeight =
      (configuration.raisedHeight == null && configuration.location?.locationType === TypeLocationEnum.concrete) ||
      (configuration.raisedHeight != null && configuration.location?.locationType !== TypeLocationEnum.concrete);

    let checkConfigurationSides =
      configuration.configurationSides?.filter(s => s.length <= 0 || !s.length).length === 0 &&
      configuration.configurationSides
        ?.filter(s => s.hasFinishing)
        .filter(s => s.finishingLength <= 0 || !s.length || s.finishingLength > s.length).length === 0;

    let checkFinishing = false;

    if (this.configuration.deckingBoardType != null && this.configuration.deckingBoard != null) {
      if (this.configuration.configurationSides?.filter(s => s.hasFinishing).length > 0) {
        if (
          this.configuration.deckingBoardType.nameFr === 'Composite' &&
          this.configuration.deckingBoardFinishing != null
        ) {
          checkFinishing = true;
        } else if (
          this.configuration.deckingBoardType.nameFr === 'Bois' &&
          this.configuration.deckingBoardFinishing == null
        ) {
          checkFinishing = true;
        }
      } else {
        checkFinishing = true;
      }
    }

    this.configuration.isComplete =
      this.configuration.location != null &&
      checkRaisedHeight &&
      configuration.shape != null &&
      checkConfigurationSides &&
      this.configuration.layingType != null &&
      this.configuration.deckingBoardType != null &&
      this.configuration.deckingBoard != null &&
      checkFinishing;
  }

  /**
   * Fonction de check si la page est accessible ou non
   * @param step étape de la configuration
   * @returns boolean
   */
  /*checkPageChangeCapability(step: number): boolean {
    switch (step) {
      case 1:
        return this.configuration.location != null;

      case 2:
        return this.configuration.location != null
          && (this.configuration.location.locationType === TypeLocationEnum.concrete ||
            (this.configuration.location.locationType !== TypeLocationEnum.concrete
              && this.configuration.raisedHeight != null));

      case 3:
        return this.configuration.shape != null && this.configuration.layingType != null
          && this.configuration.configurationSides?.filter(s => s.length <= 0 || !s.length).length === 0
          && this.configuration.configurationSides?.filter(s => s.hasFinishing).filter(s => s.finishingLength <= 0 || s.finishingLength > s.length).length === 0;

      case 4:
        return this.checkRequirementStep4();

      case 5:
        return this.checkRequirementStep4();

      default:
        return false;
    }
  }*/

  /**
   * Fonction d'initialisation de chaque page
   * @param step étape de la configuration
   * @returns promise
   */
  /*initPage(step: number): Promise<boolean> {
    return new Promise((resolve) => {
      switch (step) {
        case 1:
          resolve(this.configuration.location != null);
          break;

        case 2:
          resolve(this.configuration.location != null
            && (this.configuration.location.locationType === TypeLocationEnum.concrete ||
              (this.configuration.location.locationType !== TypeLocationEnum.concrete
                && this.configuration.raisedHeight != null)));
          break;

        case 3:
          resolve(this.configuration?.shape != null && this.configuration?.layingType != null);
          break;

        case 4:
          resolve(this.checkRequirementStep4());
          break;

        case 5:
          this.checkIsComplete();
          resolve(this.checkRequirementStep4());
          break;

        default:
          resolve(true);
          break;
      }
    })
  }*/

  /**
   * Fonction d'activation du bouton suivant
   * @returns booléen d'activation du bouton suivant
   */
  /*isNextButtonActive(step: number): boolean {
    switch (step) {
      case 1:
        return this.configuration.location != null
          && (this.configuration.location.locationType === TypeLocationEnum.concrete ||
            (this.configuration.location.locationType !== TypeLocationEnum.concrete
              && this.configuration.raisedHeight != null));

      case 2:
        return this.configuration.shape != null && this.configuration.layingType != null
          && this.configuration.configurationSides?.filter(s => s.length <= 0 || !s.length).length === 0
          && this.configuration.configurationSides?.filter(s => s.hasFinishing).filter(s => s.finishingLength <= 0 || s.finishingLength > s.length).length === 0;

      case 3:
        return this.checkRequirementStep4();

      case 4:
        return this.checkRequirementStep4();

      case 5:
        return true;

      default:
        return false;
    }
  }*/

  /**
   * Check si on peut afficher la page 4
   */
  /*checkRequirementStep4(): boolean {
    let hasDeckingBoard = this.configuration?.deckingBoardType != null
      && this.configuration?.deckingBoard != null;

    // deckingboardtype et deckingboard sélectionnées, et finitions demandées
    if (hasDeckingBoard && this.configuration?.configurationSides.filter(s => s.hasFinishing).length > 0) {
      // type choisi peut avoir des finitions
      if (this.deckingBoardsService.deckingBoards
        .filter(d => d.deckingBoardTypeId === this.configuration?.deckingBoardType.id
          && this.deckingBoardsService.deckingBoardFinishings
            .filter(f => f.deckingBoardId === d.id).length > 0).length > 0) {
        return this.configuration?.deckingBoardFinishing != null;
      }
    }

    return hasDeckingBoard;
  }*/
}
